// src/App.tsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import MobileApp from "./MobileApp";
import { DateTime } from "luxon";
import { TimezoneProvider, detectTimezone } from "./lib/TimezoneContext";
import {
  DEFAULT_RESET_RULE,
  ResetRule,
  formatCountdownToMs,
  formatHomeRaidSessionsLabel,
  formatIanaTzShortLabel,
  formatScheduleRaidTimes,
  getNextResetWeek,
  resetInstantForWeek,
} from "./lib/time";
import AdminPanel from "./AdminPanel";
import RosterPage from "./RosterPage";
import GuildsPage from "./GuildsPage";
import InviteLandingPage from "./InviteLandingPage";
import JoinRequestPage from "./JoinRequestPage";
import AlignmentFormPage from "./AlignmentFormPage";
import AlignmentBoardPage from "./AlignmentBoardPage";
import AlignmentPlayerView from "./AlignmentPlayerView";
import PugSoftReservePage from "./PugSoftReservePage";
import LandingPage from "./LandingPage";
import LootQueueArena from "./components/LootQueueArena";
import { ConfirmProvider } from "./ConfirmDialog";
import { ToastProvider } from "./Toast";

import PlannerPage, { ScheduledRaid } from "./PlannerPage";
import { CreateRaidGuild, CreateRaidCharacter } from "./CreateRaidPage";
import MilpResultsPage from "./MilpResultsPage";

import BugsAdminPage from "./BugsAdminPage";
import BugReportButton from "./BugReportButton";
import BugReportModal from "./BugReportModal";
import RaidChatSidebar from "./RaidChatSidebar";
import AdminRatingsPage from "./AdminRatingsPage";
import AnnouncementsFeed from "./AnnouncementsFeed";
import CrossGuildInvitesHome from "./CrossGuildInvitesHome";
import OpenSeatsHome from "./OpenSeatsHome";
import WeeklyLockouts from "./WeeklyLockouts";
import GuildCommandCenter, { ManagedGuild } from "./GuildCommandCenter";
import PlayerReadyBar from "./PlayerReadyBar";
import AnnouncementsAdminPage from "./AnnouncementsAdminPage";
import PatchNotesAdminPage from "./PatchNotesAdminPage";
import GuildStatsAdminPage from "./GuildStatsAdminPage";
import PatchNotesViewer from "./PatchNotesViewer";
import TutorialHelper from "./TutorialHelper";
import OnboardingTour, { requestOnboardingTour } from "./OnboardingTour";
import TrialOnboarding from "./TrialOnboarding";
import { isReplayPending, loadDraft, clearDraft, clearReplayPending } from "./trialReplay";
import WaitlistPage from "./WaitlistPage";
import WaitlistAdminPage from "./WaitlistAdminPage";

import { API_URL } from './config';
import { ThemedPage, ThemeToggle, LegibleSpan, ThemeScope } from "./theme/ThemedPage";
import { ThemeProvider, useTheme } from "./theme/ThemeProvider";
import { R } from "./theme/tokens";
import { DISPLAY, BODY } from "./alignmentUi";

import { clarity } from "react-microsoft-clarity";
import { shortGameName } from "./lib/gameName";
// --- Types ---

type Me = {
  id: string;
  discord_id: string;
  username: string;
  avatar: string | null;
  roles?: string[];
  access_tier?: string;
} | null;

// --- Constants ---

const TABS = ["Home", "Roster", "Guilds", "Planner", "Alignment", "Find", "MILP", "Bugs", "Reports", "Announce", "Patches", "Stats", "Waitlist", "Admin"] as const;
const LFG_TAGS = ["All", "Casual", "Dungeons", "Farming", "PvP", "Recruiting", "Leveling", "World Bosses", "Other"] as const;
const TOTAL_AVAIL_SLOTS = 7 * 48; // 336 slots

// Guild application links come in two shapes that mean the same thing:
//   #JoinRequest?token=<long>  (the /ralli join wizard's per-person link)
//   #join/<code>               (the short shareable form for open guild links)
// Returns the token for either, or null. Keep both: wizard links already in
// Discord must not break when the short form is introduced.
const JOIN_HASH_PREFIXES = ["JoinRequest", "join/"];
function parseJoinToken(hash: string): string | null {
  if (hash.startsWith("join/")) {
    return decodeURIComponent(hash.slice("join/".length).split(/[?#]/)[0]) || null;
  }
  if (hash.startsWith("JoinRequest")) {
    return new URLSearchParams(hash.split("?")[1]).get("token");
  }
  return null;
}
const isJoinHash = (hash: string) => JOIN_HASH_PREFIXES.some(p => hash.startsWith(p));

// --- LFG Types ---
interface LfgListing {
  id: string;
  name: string;
  description: string;
  discord_link: string;
  tag: string;
  image_url: string | null;
  created_at: string;
  expires_at: string;
  character_name: string;
  character_class: string;
  character_realm: string;
}

// --- Helper Functions ---

function bitstringToBoolArray(bits: string | null | undefined): boolean[] {
  const arr = new Array(TOTAL_AVAIL_SLOTS).fill(false);
  if (!bits) return arr;
  const len = Math.min(bits.length, TOTAL_AVAIL_SLOTS);
  for (let i = 0; i < len; i++) {
    arr[i] = bits[i] === "1";
  }
  return arr;
}

// (A private getNextWednesday() lived here and was never called — removed rather
// than converted, since it was a fourth copy of the reset rule with no readers.)

/**
 * Nag for next week's availability once the current week is nearly over.
 *
 * Was a hardcoded "show on Sat/Sun/Mon/Tue" list, which is the last four days of
 * a WEDNESDAY reset week. That silently becomes the wrong four days for a US
 * player, whose week ends Monday — they would still be nagged on Tuesday, after
 * their reset had already happened. Expressed as "within N days of this
 * viewer's next reset" it is correct for any region.
 */
const CONFIRM_BANNER_DAYS_BEFORE_RESET = 4;

function shouldShowConfirmationBanner(rule: ResetRule = DEFAULT_RESET_RULE): boolean {
  const nextReset = resetInstantForWeek(getNextResetWeek(rule), rule);
  const daysLeft = nextReset.diff(DateTime.utc(), 'days').days;
  return daysLeft <= CONFIRM_BANNER_DAYS_BEFORE_RESET;
}

function mapGameVersion(dbName: string): string {
  return shortGameName(dbName) || "Classic Anniversary";
}

function formatWeekRangeFromStart(weekStart: string | undefined | null): string {
  if (!weekStart) return 'Unknown';
  const wed = new Date(weekStart + 'T00:00:00Z');
  const tue = new Date(wed);
  tue.setDate(tue.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(wed)} - ${fmt(tue)}`;
}

function mapMyRaidsApiRowToScheduled(row: any, userTz: string): ScheduledRaid {
  let anchorDate = new Date();
  if (row.sessions?.[0]?.starts_at) {
    anchorDate = new Date(row.sessions[0].starts_at);
  } else if (row.anchor_date) {
    const ymd = String(row.anchor_date).slice(0, 10);
    const lux = DateTime.fromISO(ymd, { zone: "utc" }).startOf("day");
    anchorDate = lux.isValid ? lux.toJSDate() : new Date();
  }

  const localTimeLabel = formatHomeRaidSessionsLabel(row.sessions, row.anchor_date, userTz);

  const meta = row.metadata || {};
  const guildMeta = row.guild_metadata || {};
  const assign = row.my_assignment || {};

  let gLogo = row.guild_logo_url || guildMeta.logo_url || null;
  if (gLogo && !gLogo.startsWith("http")) gLogo = `${API_URL}${gLogo}`;

  let cImg = assign.render_url || assign.avatar_url || assign.image_url || null;
  if (cImg && !cImg.startsWith("http")) cImg = `${API_URL}${cImg}`;

  const endDate = (() => {
    const sess = row.sessions;
    if (!sess || !Array.isArray(sess) || sess.length === 0) {
      return new Date(anchorDate.getTime() + 3 * 60 * 60 * 1000);
    }
    let latestEnd = 0;
    for (const s of sess) {
      const st = new Date(s.starts_at).getTime();
      const dur = (s.duration || 180) * 60 * 1000;
      if (!isNaN(st)) latestEnd = Math.max(latestEnd, st + dur);
    }
    return latestEnd > 0 ? new Date(latestEnd) : new Date(anchorDate.getTime() + 3 * 60 * 60 * 1000);
  })();

  return {
    id: String(row.id),
    gameVersion: mapGameVersion(row.game_name),
    region: guildMeta.region === "US" ? "US" : "EU",
    realm: row.guild_server_name || "Unknown",
    raidName:
      row.raid_instances && row.raid_instances.length > 1
        ? row.raid_instances.map((ri: any) => ri.label).join(" + ")
        : row.raid_name || row.raid_label || "Unknown raid",
    difficulty: "Normal",
    size: (Number(meta.size) || 10) as 10 | 25 | 40,

    localTimeLabel,
    anchorDate,
    endDate,

    roleSummary: "View Planner",
    raidLeader: row.raid_leader_name || "TBD",
    lootMaster: row.loot_master_name || "TBD",
    hostedByGuild: row.guild_tag || row.guild_name || "Unknown",
    guild_id: row.guild_id,
    guildLogo: gLogo,

    seriesId: row.series_id,
    sessions: row.sessions || [],

    myCharName: assign.name,
    myCharClass: assign.class_name,
    myCharRole: assign.role,
    myCharImage: cImg,

    raidImageUrl: row.raid_image_url || null,

    srEnabled: !!row.soft_reserve_info?.enabled,
    srLocked: row.soft_reserve_info?.locked || false,
    srMyCount: row.soft_reserve_info?.my_count || 0,
    srMax: row.soft_reserve_info?.max_reserves || 0,
    srMyItems: row.soft_reserve_info?.my_items || null,
  };
}

// WoW class colors
const CLASS_COLORS: Record<string, string> = {
  'Warrior': '#C79C6E',
  'Paladin': '#F58CBA',
  'Hunter': '#ABD473',
  'Rogue': '#FFF569',
  'Priest': '#FFFFFF',
  'Death Knight': '#C41F3B',
  'Shaman': '#0070DE',
  'Mage': '#69CCF0',
  'Warlock': '#9482C9',
  'Monk': '#00FF96',
  'Druid': '#FF7D0A',
  'Demon Hunter': '#A330C9',
  'Evoker': '#33937F',
};

function getTimeRemaining(expiresAt: string): string {
  const now = new Date();
  const expires = new Date(expiresAt);
  const diffMs = expires.getTime() - now.getTime();
  
  if (diffMs <= 0) return 'Expired';
  
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0) return `${hours}h ${mins}m left`;
  return `${mins}m left`;
}

export default function LfmLauncher() {
const [localNow, setLocalNow] = useState<Date>(() => new Date());
// --- Clarity Analytics ---
useEffect(() => {
  // This grabs the ID "uyobqvpolj" from Render automatically
  const clarityId = process.env.REACT_APP_CLARITY_ID;
  if (clarityId) clarity.init(clarityId);
}, []);

useEffect(() => {
  const intervalId = window.setInterval(() => {
    setLocalNow(new Date());
  }, 30000);
  return () => window.clearInterval(intervalId);
}, []);

  // --- UI State ---
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<(typeof LFG_TAGS)[number]>("All");
  const [isBugModalOpen, setIsBugModalOpen] = useState(false);
  
  // --- LFG State ---
  const [lfgListings, setLfgListings] = useState<LfgListing[]>([]);
  const [lfgLoading, setLfgLoading] = useState(false);
  const [showCreateLfgModal, setShowCreateLfgModal] = useState(false);
  const [selectedLfgListing, setSelectedLfgListing] = useState<LfgListing | null>(null);

  // --- Loot Queue Arena (joiner) --- path-based route /arena/<sessionId>.
  // No persistence beyond the session id in the path (per spec §1.3).
  const [arenaSession, setArenaSession] = useState<string | null>(() => {
    const m = window.location.pathname.match(/^\/arena\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  });
  const [arenaGuestId] = useState(() => `guest_${Math.random().toString(36).slice(2)}`);

  // --- Alignment Board share link --- path-based route /a/<shareToken>.
  // Discord's OAuth callback returns to the site root, so the token has to
  // survive the bounce in sessionStorage, same as the invite and join-request
  // links. Without that a player signs in and lands on the dashboard with no
  // idea what happened to the form they were filling in.
  const [alignmentToken, setAlignmentToken] = useState<string | null>(() => {
    const m = window.location.pathname.match(/^\/a\/([^/?#]+)/);
    if (m) return decodeURIComponent(m[1]);
    return sessionStorage.getItem("pending_alignment_token");
  });
  useEffect(() => {
    const onPop = () => {
      const m = window.location.pathname.match(/^\/arena\/([^/?#]+)/);
      setArenaSession(m ? decodeURIComponent(m[1]) : null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const [inviteCode, setInviteCode] = useState<string | null>(() => {
    const pending = sessionStorage.getItem("pending_invite_code");
    if (pending) return pending;
    const hash = window.location.hash.replace("#", "");
    if (hash.startsWith("Invite")) {
      const params = new URLSearchParams(hash.split("?")[1]);
      return params.get("code");
    }
    return null;
  });

  // Guild application links, two shapes for one flow:
  //   #JoinRequest?token=<long>  - minted by the /ralli join Discord wizard
  //   #join/<code>               - short shareable form for open guild links
  // Both resolve to the same token, so everything downstream (including the
  // waitlist gate, which keys on the parsed token rather than the hash) is
  // unchanged. The long form must keep working: wizard links live for 2 hours
  // and may already be sitting in someone's Discord.
  // sessionStorage carries it across the Discord OAuth bounce, same as invites.
  const [joinRequestToken, setJoinRequestToken] = useState<string | null>(() => {
    const pending = sessionStorage.getItem("pending_join_request_token");
    if (pending) return pending;
    const hash = window.location.hash.replace("#", "");
    return parseJoinToken(hash);
  });

  const [pugSrData, setPugSrData] = useState<{ raidId: string; token: string } | null>(() => {
    const pending = sessionStorage.getItem("pending_pug_sr");
    if (pending) {
      try { return JSON.parse(pending); } catch (_) {}
    }
    const hash = window.location.hash.replace("#", "");
    if (hash.startsWith("PugSR")) {
      const params = new URLSearchParams(hash.split("?")[1]);
      const raidId = params.get("raid");
      const token = params.get("token");
      if (raidId && token) return { raidId, token };
    }
    return null;
  });

  // Pending pug invite code needing async resolution
  const [pendingPugCode, setPendingPugCode] = useState<string | null>(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash.startsWith("pug/")) return hash.slice(4).split("?")[0];
    return null;
  });

  const [activeTab, setActiveTab] = useState<string>(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash.startsWith("Invite")) {
      const params = new URLSearchParams(hash.split("?")[1]);
      if (params.get("code")) return "Invite";
    }
    if (hash.startsWith("PugSR")) {
      const params = new URLSearchParams(hash.split("?")[1]);
      if (params.get("raid") && params.get("token")) return "PugSR";
    }
    if (hash.startsWith("pug/")) return "PugSR";
    if (isJoinHash(hash) && parseJoinToken(hash)) return "JoinRequest";
    const validTabs = TABS as readonly string[];
    const hashBase = hash.split("?")[0];
    if (hashBase && (validTabs as readonly string[]).includes(hashBase)) return hashBase;
    if (sessionStorage.getItem("pending_invite_code")) return "Invite";
    if (sessionStorage.getItem("pending_join_request_token")) return "JoinRequest";
    if (sessionStorage.getItem("pending_pug_sr")) return "PugSR";
    return "Home";
  });
// Resolve short pug invite code → { raidId, token }
React.useEffect(() => {
  if (!pendingPugCode) return;
  (async () => {
    try {
      const res = await fetch(`/raids/pug-resolve/${encodeURIComponent(pendingPugCode)}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setPugSrData({ raidId: data.raidId, token: data.token });
    } catch {
      setActiveTab("Home");
      window.location.hash = "Home";
    } finally {
      setPendingPugCode(null);
    }
  })();
}, [pendingPugCode]);

  // --- Data State ---
  const [me, setMe] = useState<Me>(null);
  const [authChecked, setAuthChecked] = useState(false);
  // "Try it" guest onboarding. `trialActive` = the no-account walkthrough
  // (launched from the Landing page). `trialReplay` = the post-Discord-bounce
  // replay of the captured draft (see trialOnboarding.ts).
  const [trialActive, setTrialActive] = useState(false);
  const [trialReplay, setTrialReplay] = useState(false);
  const [characters, setCharacters] = useState<CreateRaidCharacter[]>([]);
  const [plannerGuilds, setPlannerGuilds] = useState<CreateRaidGuild[]>([]);
  // Admin "view as guild" mode: a Rallify admin opened a guild's planner from
  // the Guild Stats tab. While set, the Planner shows that guild's raids and a
  // warning banner stays pinned above the main content.
  const [adminViewGuild, setAdminViewGuild] = useState<CreateRaidGuild | null>(null);
  const [managedGuilds, setManagedGuilds] = useState<ManagedGuild[]>([]);
  const [myRaidsRaw, setMyRaidsRaw] = useState<any[]>([]);
  const [tzSnapshot, setTzSnapshot] = useState(() => detectTimezone());
  useEffect(() => {
    const onFocus = () => setTzSnapshot(detectTimezone());
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);
  const [guildRaidOverview, setGuildRaidOverview] = useState<any[]>([]);
  const [hasEverRaided, setHasEverRaided] = useState(false);
  const [accountAvailability, setAccountAvailability] = useState<boolean[]>(
    () => new Array(TOTAL_AVAIL_SLOTS).fill(false)
  );
  const [availabilityConfirmation, setAvailabilityConfirmation] = useState<{
    confirmedWeekStart: string | null;
    confirmedAt: string | null;
    confirmedVia: string | null;
  }>({ confirmedWeekStart: null, confirmedAt: null, confirmedVia: null });

  // New weekly availability state
  const [weeklyAvailability, setWeeklyAvailability] = useState<{
    currentWeek: {
      weekStart: string;
      weekEnd: string;
      slots: string;
      confirmedAt: string | null;
      confirmedVia: string | null;
    } | null;
    nextWeek: {
      weekStart: string;
      weekEnd: string;
      slots: string;
      confirmedAt: string | null;
      confirmedVia: string | null;
      copiedFromWeek?: string;
      isAutoCopied?: boolean;
    } | null;
  }>({ currentWeek: null, nextWeek: null });  

  // Identify the user
  useEffect(() => {
    if (me && clarity.hasStarted()) {
      clarity.identify(me.id, {
        username: me.username,
        discord_id: me.discord_id,
        role: me.roles?.join(", ") || "Member"
      });
    }
  }, [me]);
  
  // VERSIONING: Used to trigger data refreshes without page reload
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [dataVersion, setDataVersion] = useState(0);

  const isAdmin = useMemo(
    () => !!me && Array.isArray(me.roles) && me.roles.some((r) => r === "admin" || r === "super_admin"),
    [me]
  );

  const handleSameAsLastWeek = async () => {
    try {
      const res = await fetch("/me/availability/copy-to-next", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" }
      });
      
      if (res.ok) {
        // Refresh availability data
        const availRes = await fetch("/me/availability", { credentials: "include" });
        if (availRes.ok) {
          const availData = await availRes.json();
          if (availData.currentWeek || availData.nextWeek) {
            setWeeklyAvailability({
              currentWeek: availData.currentWeek,
              nextWeek: availData.nextWeek
            });
          }
          if (availData.confirmedWeekStart) {
            setAvailabilityConfirmation({
              confirmedWeekStart: availData.confirmedWeekStart,
              confirmedAt: availData.confirmedAt,
              confirmedVia: availData.confirmedVia
            });
          }
        }
      }
    } catch (err) {
      console.error('Failed to copy availability:', err);
    }
  };
  // --- Effects ---

  // 1. Auth Check (Run Once)
  useEffect(() => {
    fetch("/me", { credentials: "include" })
      .then((r) => r.json())
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setAuthChecked(true));
  }, []);

  // After the Discord bounce, a kept trial lands back here with a fresh session
  // and a pending draft → replay it through the real endpoints (and surface the
  // Discord-bot explainer). sessionStorage survives the same-tab OAuth redirect.
  useEffect(() => {
    if (authChecked && me && isReplayPending() && loadDraft()) {
      // Waitlisted users can't replay a trial draft (the endpoints are gated);
      // drop it so it doesn't linger and fail after they get approved later.
      if (me.access_tier === "waitlisted") {
        clearDraft();
        clearReplayPending();
        return;
      }
      setTrialReplay(true);
    }
  }, [authChecked, me]);

  // 2. Data Fetching (Runs on Auth or DataRefresh)
  useEffect(() => {
    if (!me) {
      setPlannerGuilds([]);
      setManagedGuilds([]);
      setCharacters([]);
      setMyRaidsRaw([]);
      return;
    }

    // A. Guilds
    fetch("/guilds", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows: any[]) => {
        const mapped: CreateRaidGuild[] = rows
          .filter((row) => row.isOwner || row.isOfficer || row.canCreateRaids)
          .map((row) => ({
            id: String(row.id),
            name: String(row.name),
            tag: row.tag ?? null,
            serverName: row.serverName ?? row.server_name ?? null,
            region: row.region === "US" ? "US" : "EU", 
            logoUrl: row.logoUrl ?? row.logo_url ?? null,
            gameId: row.gameId ?? row.game_id,
          }));
          setPlannerGuilds(mapped);

          // Build the home-page Command Center list from the same /guilds
          // payload — no extra request. Filtered to roles that have officer
          // tools on the dashboard (owners + officers).
          const managed: ManagedGuild[] = rows
            .filter((row: any) => row.isOwner || row.isOfficer)
            .map((row: any) => ({
              id: String(row.id),
              name: String(row.name),
              tag: row.tag ?? null,
              logoUrl: row.logoUrl ?? row.logo_url ?? null,
              isOwner: !!row.isOwner,
              isOfficer: !!row.isOfficer,
            }));
          setManagedGuilds(managed);
        })
        .catch(console.error);

    // B. Characters
    fetch("/characters", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setCharacters(list);
        // A character joined through the Discord Activity carries a marker,
        // because the Activity cannot reach this browser's tour flag. Claim it
        // and queue the tour (a no-op if they already finished it here).
        if (list.some((c: any) => c?.metadata?.tour_pending)) {
          fetch("/me/onboarding-tour/claim", { method: "POST", credentials: "include" })
            .then((r) => (r.ok ? r.json() : null))
            .then((j) => {
              if (j?.characterName) requestOnboardingTour(j.characterName);
            })
            .catch(() => {});
        }
      })
      .catch(console.error);

    // C. Availability (handles both new weekly and legacy format)
    fetch("/me/availability", { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!data) return;
        
        // New format: weekly availability
        if (data.currentWeek || data.nextWeek) {
          setWeeklyAvailability({
            currentWeek: data.currentWeek,
            nextWeek: data.nextWeek
          });
          
          // Also set legacy state for backwards compatibility
          const primarySlots = data.currentWeek?.slots || data.nextWeek?.slots;
          if (primarySlots) {
            setAccountAvailability(bitstringToBoolArray(primarySlots));
          }
        }
        
        // Legacy format support (slots at top level)
        if (data.slots && typeof data.slots === "string") {
          setAccountAvailability(bitstringToBoolArray(data.slots));
        }
        
        // Set confirmation state
        if (data.confirmedWeekStart !== undefined) {
          setAvailabilityConfirmation({
            confirmedWeekStart: data.confirmedWeekStart,
            confirmedAt: data.confirmedAt,
            confirmedVia: data.confirmedVia
          });
        }
      })
      .catch(console.error);

      // D3. Has user ever been in a raid?
    fetch("/my/has-raided", { credentials: "include" })
    .then((r) => (r.ok ? r.json() : { hasRaided: false }))
    .then((data: any) => setHasEverRaided(data.hasRaided === true))
    .catch(() => {});

    // D2. Guild raids user is NOT assigned to
    fetch("/my/guild-raids-overview", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows: any[]) => setGuildRaidOverview(rows || []))
      .catch(() => setGuildRaidOverview([]));

    // D. Upcoming Raids (raw rows; display mapping uses tzSnapshot + useMemo)
    fetch("/my/raids", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows: any[]) => {
        setMyRaidsRaw(Array.isArray(rows) ? rows : []);
      })
      .catch(() => setMyRaidsRaw([]));

  }, [me, dataVersion]); // <--- Re-run when dataVersion changes

  const scheduledRaids = useMemo(() => {
    const mapped = myRaidsRaw.map((row) => mapMyRaidsApiRowToScheduled(row, tzSnapshot));
    const now = localNow.getTime();
    return mapped.filter((r) => now < r.endDate.getTime());
  }, [myRaidsRaw, tzSnapshot, localNow]);

  const scheduleTzShort = formatIanaTzShortLabel(tzSnapshot);
  const homeClockInTz = DateTime.fromJSDate(localNow).setZone(tzSnapshot).toFormat("HH:mm");

  // Callback to trigger a background refresh
  const handleDataUpdate = useCallback(() => {
    setDataVersion(v => v + 1);
  }, []);

  useEffect(() => {
    function handleHashChange() {
      const hash = window.location.hash.replace("#", "");
      if (hash.startsWith("Invite")) {
        const params = new URLSearchParams(hash.split("?")[1]);
        const code = params.get("code");
        if (code) {
          setInviteCode(code);
          setActiveTab("Invite");
          return;
        }
      }
      if (hash.startsWith("pug/")) {
        const code = hash.slice(4).split("?")[0];
        setPendingPugCode(code);
        setActiveTab("PugSR");
        return;
      }
      if (isJoinHash(hash)) {
        const token = parseJoinToken(hash);
        if (token) {
          setJoinRequestToken(token);
          setActiveTab("JoinRequest");
          return;
        }
      }
      if (hash.startsWith("PugSR")) {
        const params = new URLSearchParams(hash.split("?")[1]);
        const raidId = params.get("raid");
        const token = params.get("token");
        if (raidId && token) {
          setPugSrData({ raidId, token });
          setActiveTab("PugSR");
          return;
        }
      }
      // Handle Roster?scrollTo=availability from Discord bot links
      const [hashBase, hashQuery] = hash.split("?");
      const hashParams = hashQuery ? new URLSearchParams(hashQuery) : null;
      
      const validTabs = TABS as readonly string[];
      if (hashBase && (validTabs as readonly string[]).includes(hashBase)) {
        setActiveTab(hashBase as (typeof TABS)[number]);
        if (hashBase === "Home") setDataVersion(v => v + 1);
        // Scroll to element if scrollTo param is present
        if (hashParams?.get("scrollTo")) {
          const scrollTarget = hashParams.get("scrollTo");
          setTimeout(() => {
            const el = document.getElementById(scrollTarget || "");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 500);
        }
      } else if (!hash.startsWith("Invite") && !hash.startsWith("PugSR") && !isJoinHash(hash)) {
        setActiveTab("Home");
        setDataVersion(v => v + 1);
      }
    }

    const pendingPugSr = sessionStorage.getItem("pending_pug_sr");
    if (pendingPugSr) {
        try {
          const data = JSON.parse(pendingPugSr);
          sessionStorage.removeItem("pending_pug_sr");
          setPugSrData(data);
          setActiveTab("PugSR");
          window.location.hash = 'PugSR';
        } catch (_) { sessionStorage.removeItem("pending_pug_sr"); }
    }

    const pending = sessionStorage.getItem("pending_invite_code");
    const pendingJoinToken = sessionStorage.getItem("pending_join_request_token");
    if (pending) {
        setInviteCode(pending);
        setActiveTab("Invite");
        window.location.hash = `Invite?code=${pending}`;
    } else if (pendingJoinToken) {
        setJoinRequestToken(pendingJoinToken);
        setActiveTab("JoinRequest");
        window.location.hash = `JoinRequest?token=${pendingJoinToken}`;
    } else if (!pendingPugSr) {
        handleHashChange();
    }

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  // Handle scrollTo param on initial load (e.g., from Discord bot "Review Availability" link)
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    const hashQuery = hash.split("?")[1];
    if (hashQuery) {
      const params = new URLSearchParams(hashQuery);
      const scrollTarget = params.get("scrollTo");
      if (scrollTarget) {
        setTimeout(() => {
          const el = document.getElementById(scrollTarget);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 1000); // Longer delay on initial load to ensure DOM is ready
      }
    }
  }, []);

  useEffect(() => {
    if (!isAdmin && activeTab === "Admin") {
      setActiveTab("Home");
      window.location.hash = "Home";
    }
  }, [isAdmin, activeTab]);

  // Drop admin guild view if the user loses (or never had) admin rights
  useEffect(() => {
    if (!isAdmin && adminViewGuild) setAdminViewGuild(null);
  }, [isAdmin, adminViewGuild]);

// Refresh data when navigating to tabs that need fresh data
  // This makes the site work like every other website - data refreshes on page visit
  useEffect(() => {
    // Tabs that need fresh user data (characters, guilds, raids, etc.)
    const tabsNeedingRefresh = ["Home", "Roster", "Guilds", "Planner", "MILP"];
    if (tabsNeedingRefresh.includes(activeTab)) {
      setDataVersion(v => v + 1);
    }
  }, [activeTab]);

  async function logout() {
    try { await fetch("/logout", { method: "POST", credentials: "include" }); } catch (e) { console.error(e); }
    setMe(null);
    window.location.reload();
  }

  // Fetch LFG listings
  const fetchLfgListings = useCallback(async () => {
    setLfgLoading(true);
    try {
      const res = await fetch('/api/lfg');
      if (res.ok) {
        const data = await res.json();
        setLfgListings(data);
      }
    } catch (err) {
      console.error('Failed to fetch LFG listings:', err);
    } finally {
      setLfgLoading(false);
    }
  }, []);

  // Fetch listings when Find tab is active
  useEffect(() => {
    if (activeTab === "Find") {
      fetchLfgListings();
    }
  }, [activeTab, fetchLfgListings]);

  const nextRaid = scheduledRaids[0] || null;
  const isNextRaidLive = nextRaid ? (new Date().getTime() >= nextRaid.anchorDate.getTime() && new Date().getTime() < nextRaid.endDate.getTime()) : false;
  const filteredListings = lfgListings.filter((l) => {
    const nameOk = l.name.toLowerCase().includes(query.toLowerCase()) || 
                   l.description.toLowerCase().includes(query.toLowerCase());
    const filterOk = activeFilter === "All" ? true : l.tag === activeFilter;
    return nameOk && filterOk;
  });

  // Alignment Board share link. Short-circuits the app shell: this is a public
  // page an officer hands to people who may have no guild here and no reason to
  // see a dashboard. It renders signed out too, showing only the guild name.
  if (alignmentToken) {
    if (!authChecked) {
      return (
        <div className="fixed inset-0 flex items-center justify-center bg-[#0D1014] text-white/50 text-sm">
          Loading.
        </div>
      );
    }
    return (
      <TimezoneProvider>
        <ToastProvider>
          <ConfirmProvider>
            <AlignmentFormPage
              token={alignmentToken}
              me={me as any}
              onDone={() => {
                sessionStorage.removeItem("pending_alignment_token");
                window.history.pushState({}, "", "/#Home");
                setAlignmentToken(null);
              }}
            />
          </ConfirmProvider>
        </ToastProvider>
      </TimezoneProvider>
    );
  }

  // Loot Queue Arena joiner — path-based /arena/<sessionId>. Renders the overlay
  // for anyone who opens the link inside the app, using their logged-in identity.
  // Short-circuits the normal app shell (works on mobile + desktop alike).
  if (arenaSession) {
    if (!authChecked) {
      return (
        <div className="fixed inset-0 flex items-center justify-center bg-[#04060A] text-white/50 text-sm">
          Loading arena…
        </div>
      );
    }
    const arenaIdentity = {
      id: me?.id ? String(me.id) : arenaGuestId,
      name: characters[0]?.name || me?.username || "Guest",
      className: characters[0]?.className || null,
    };
    return (
      <TimezoneProvider>
        <ToastProvider>
          <ConfirmProvider>
            <LootQueueArena
              mode="joiner"
              sessionId={arenaSession}
              identity={arenaIdentity}
              onClose={() => {
                window.history.pushState({}, "", "/#Home");
                setArenaSession(null);
              }}
            />
          </ConfirmProvider>
        </ToastProvider>
      </TimezoneProvider>
    );
  }

  // Post-signup replay of a kept trial. Full-screen, works on any device.
  if (trialReplay) {
    const finishReplay = () => {
      setTrialReplay(false);
      setActiveTab("Roster");
      window.location.hash = "Roster";
      setDataVersion((v) => v + 1);
    };
    return (
      <TimezoneProvider>
        <ToastProvider>
          <ConfirmProvider>
            <TrialOnboarding mode="replay" onExit={finishReplay} onComplete={finishReplay} />
          </ConfirmProvider>
        </ToastProvider>
      </TimezoneProvider>
    );
  }

  // Guest "Try it" walkthrough, launched from the Landing page, no account.
  if (trialActive && !me) {
    return (
      <TimezoneProvider>
        <ToastProvider>
          <ConfirmProvider>
            <TrialOnboarding
              mode="trial"
              onExit={() => setTrialActive(false)}
              onComplete={() => setTrialActive(false)}
            />
          </ConfirmProvider>
        </ToastProvider>
      </TimezoneProvider>
    );
  }

  // Waitlist gate: waitlisted users only see the waitlist page, unless they are
  // mid guild-invite or PUG flow (those joins promote them server-side, after
  // which the success handlers reload to refetch /me).
  if (
    authChecked &&
    me &&
    me.access_tier === "waitlisted" &&
    !inviteCode &&
    !pugSrData &&
    !pendingPugCode &&
    !joinRequestToken &&
    !sessionStorage.getItem("pending_invite_code") &&
    !sessionStorage.getItem("pending_join_request_token") &&
    !sessionStorage.getItem("pending_pug_sr")
  ) {
    return <WaitlistPage me={me} />;
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <TimezoneProvider>
        <ToastProvider>
          <ConfirmProvider>
          <MobileApp
              me={me}
              onLogout={logout}
              inviteCode={inviteCode}
              onInviteComplete={() => {
                if (me?.access_tier === "waitlisted") {
                  window.location.hash = "Guilds";
                  window.location.reload();
                  return;
                }
                setInviteCode(null);
                window.location.hash = "Guilds";
                setActiveTab("Guilds");
              }}
              pugSrData={pugSrData}
              onPugComplete={() => {
                sessionStorage.removeItem("pending_pug_sr");
                if (me?.access_tier === "waitlisted") {
                  window.location.hash = "Home";
                  window.location.reload();
                  return;
                }
                setPugSrData(null);
                window.location.hash = "Home";
                setActiveTab("Home");
              }}
              joinRequestToken={joinRequestToken}
              onJoinRequestComplete={() => {
                sessionStorage.removeItem("pending_join_request_token");
                if (me?.access_tier === "waitlisted") {
                  window.location.hash = "Home";
                  window.location.reload();
                  return;
                }
                setJoinRequestToken(null);
                window.location.hash = "Home";
                setActiveTab("Home");
              }}
            />
          </ConfirmProvider>
        </ToastProvider>
      </TimezoneProvider>
    );
  }

  // Tabs whose page is on theme tokens. The header and page background follow
  // the light/dark switch only here; elsewhere they stay dark so no page is
  // ever half light, half dark. Add a tab once its page is migrated.
  const themedTab =
    activeTab === "Planner" || activeTab === "Roster" || activeTab === "Find" || activeTab === "Alignment" ||
    activeTab === "Guilds" ||
    (isAdmin && ["Admin", "Waitlist", "MILP", "Bugs", "Reports", "Announce", "Patches", "Stats"].includes(activeTab)) ||
    (activeTab === "Home" && !!me);

  return (
    <ThemeProvider>
    <TimezoneProvider>
      <ToastProvider>
      <ConfirmProvider>
      <div className="min-h-screen w-full relative text-white overflow-x-hidden">
        {themedTab ? (
          <ThemeScope themed className="absolute inset-0 -z-10 bg-[var(--c-bg)]" />
        ) : (
          <Background />
        )}

        {/* HEADER */}
        <ThemeScope themed={themedTab} as="header" className="fixed top-0 left-0 right-0 z-50 w-full">
          <div className="relative" style={BODY}>
            {/* Taller (h-20) with Fixed Positioning */}
            <div className="h-20 w-full border-b border-[var(--c-line-soft)] bg-[var(--c-bg-veil)] backdrop-blur-2xl shadow-[var(--c-shadow-sm)] transition-all duration-300">
              <div className="mx-auto px-6 lg:px-10 h-full flex items-center justify-between relative">
                
                {/* Left Side: Logo */}
                <div className="flex items-center gap-3 group cursor-pointer" onClick={() => window.location.hash = "Home"}>
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-b from-amber-300 to-amber-500 flex items-center justify-center text-black shadow-[0_6px_20px_-8px_rgba(245,158,11,0.9)] group-hover:scale-105 transition-transform" style={DISPLAY}>
                    <span className="text-lg">R</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm tracking-wide text-[var(--c-ink)] leading-none" style={DISPLAY}>RALLIFY</span>
                    <span className="mt-1 text-[10px] text-[var(--c-ink-4)] font-semibold tracking-wider uppercase group-hover:text-[var(--c-accent-ink)] transition-colors">Less searching, more playing</span>
                  </div>
                </div>

                {/* Center: Nav - Floating Pill */}
                <nav className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 p-1.5 rounded-full border border-[var(--c-line)] bg-[var(--c-sunken)]">
                  {TABS.filter((t) => {
                    const adminTabs = ["Admin", "MILP", "Bugs", "Reports", "Announce", "Patches", "Stats", "Waitlist"];
                    if (adminTabs.includes(t)) return isAdmin;
                    return true;
                  }).map((t) => {
                    let icon = null;
                    const props = { className: "w-4 h-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor" };
                    
                    switch(t) {
                      case "Home": icon = <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>; break;
                      case "Roster": icon = <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>; break;
                      case "Guilds": icon = <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>; break;
                      case "Planner": icon = <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>; break;
                      case "Alignment": icon = <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" /></svg>; break;
                      case "Find": icon = <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>; break;
                      case "MILP": icon = <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>; break;
                      case "Bugs": icon = <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 17h.01M9 17h.01M12 13h.01M15 13h.01M9 13h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>; break;
                      case "Reports": icon = <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>; break;
                      case "Announce": icon = <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>; break;
                      case "Stats": icon = <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>; break;
                      case "Waitlist": icon = <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>; break;
                      case "Admin": icon = <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>; break;
                    }

                    return (
                      <TabButton 
                        key={t} 
                        label={t === "MILP" ? "Calendar" : t === "Roster" ? "Profile" : t}
                        icon={icon}
                        active={activeTab === t} 
                        onClick={() => { window.location.hash = t; }} 
                      />
                    );
                  })}
                  {/* Light / dark switch: last item in the pill, only on tabs
                      whose page follows the theme (elsewhere it would do
                      nothing visible). */}
                  {themedTab && (
                    <>
                      <span className="mx-1 h-5 w-px bg-[var(--c-line)]" aria-hidden="true" />
                      <ThemeToggle compact />
                    </>
                  )}
                </nav>

                {/* Right Side: Auth */}
                <div className="flex items-center gap-3">
                  {me ? (
                    <div className="flex items-center gap-3 bg-[var(--c-sunken)] p-1.5 pr-4 rounded-full border border-[var(--c-line)]">
                      {me.avatar && <img src={me.avatar} className="h-8 w-8 rounded-full border border-[var(--c-line)]" alt="Avatar" />}
                      <div className="flex flex-col items-start">
                        <span className="text-xs font-bold text-[var(--c-ink)] leading-none">{me.username}</span>
                        <button onClick={logout} className="mt-0.5 text-[10px] font-semibold text-[var(--c-ink-4)] hover:text-[var(--c-ink)] transition-colors">Sign out</button>
                      </div>
                    </div>
                  ) : (
                    <a href={`${API_URL}/auth/discord`} className={R.btnSecondary + " px-4 py-2 text-xs"}>Sign in</a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </ThemeScope>

        {/* LAYOUT */}
        <div className="relative z-10 pt-20 min-h-screen">
          
          {/* NEXT WEEK AVAILABILITY BANNER */}
          {me && characters.some(c => c.guildId) && (
            <div className={`overflow-hidden transition-all ease-in-out ${(
              !weeklyAvailability.nextWeek?.confirmedAt && 
              shouldShowConfirmationBanner()
            ) ? 'max-h-20 opacity-100 duration-300' : 'max-h-0 opacity-0 duration-700'}`}>
              <ThemeScope themed={themedTab} className="bg-[var(--c-warn-soft)] border-b border-[var(--c-warn-line)] px-4 py-3">
                <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span className="text-amber-700 dark:text-amber-400 text-lg">⚠️</span>
                    <span className="text-amber-800 dark:text-amber-200 text-sm">
                      <strong>Next week's availability</strong> needs confirmation for raid scheduling.
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch("/me/availability/copy-to-next", {
                            method: "POST",
                            credentials: "include"
                          });
                          if (res.ok) {
                            const availRes = await fetch("/me/availability", { credentials: "include" });
                            if (availRes.ok) {
                              const data = await availRes.json();
                              if (data.currentWeek || data.nextWeek) {
                                setWeeklyAvailability({ currentWeek: data.currentWeek, nextWeek: data.nextWeek });
                              }
                            }
                          }
                        } catch (err) { console.error(err); }
                      }}
                      className="px-3 py-1.5 bg-[var(--c-surface)] border border-[var(--c-line)] hover:bg-[var(--c-sunken)] text-[var(--c-ink)] text-sm rounded-lg transition-colors"
                    >
                      ✓ Same as this week
                    </button>
                    <button
                      onClick={() => setActiveTab("Roster")}
                      className="px-3 py-1.5 bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-[var(--c-ink-on-fill)] text-sm font-semibold rounded-lg transition-colors"
                    >
                      Review & Confirm
                    </button>
                  </div>
                </div>
              </ThemeScope>
            </div>
          )}

          {/* MAIN CONTENT */}
          <main className="p-5 lg:p-8">

            {/* ADMIN GUILD VIEW BANNER - pinned while admin "view as guild" mode is active */}
            {isAdmin && adminViewGuild && (
              <div className="mb-6 rounded-xl border-2 border-rose-500/60 bg-gradient-to-r from-rose-950/80 via-rose-900/60 to-rose-950/80 shadow-[0_0_25px_rgba(244,63,94,0.25)] overflow-hidden">
                <div
                  className="h-1.5 w-full"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(45deg, #f43f5e 0, #f43f5e 12px, #1c0a0f 12px, #1c0a0f 24px)",
                  }}
                />
                <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <svg className="w-6 h-6 text-rose-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <div className="flex-1 min-w-[220px]">
                    <div className="text-sm font-black uppercase tracking-widest text-rose-300">
                      Admin Mode
                    </div>
                    <div className="text-xs text-rose-100/80 font-medium">
                      Viewing <span className="font-bold text-white">{adminViewGuild.name}{adminViewGuild.tag ? ` [${adminViewGuild.tag}]` : ""}</span> as a Rallify administrator.
                      You are not a member of this guild: any changes you make here affect their real data.
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {activeTab !== "Planner" && (
                      <button
                        onClick={() => { setActiveTab("Planner"); window.location.hash = "Planner"; }}
                        className="px-3 py-1.5 bg-rose-600/30 hover:bg-rose-600/50 border border-rose-500/50 text-rose-200 text-xs font-bold rounded-lg transition"
                      >
                        Go to Planner
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setAdminViewGuild(null);
                        setActiveTab("Stats");
                        window.location.hash = "Stats";
                      }}
                      className="px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold rounded-lg transition"
                    >
                      Exit Admin View
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* PUG SOFT RESERVE PAGE */}
            {activeTab === "PugSR" && !pugSrData && pendingPugCode && (
              <div className="flex items-center justify-center h-64">
                <div className="text-white/40 text-sm animate-pulse">Loading invite...</div>
              </div>
            )}
            {activeTab === "PugSR" && pugSrData && (
              <PugSoftReservePage
                raidId={pugSrData.raidId}
                token={pugSrData.token}
                me={me}
                onDone={() => {
                  sessionStorage.removeItem("pending_pug_sr");
                  if (me?.access_tier === "waitlisted") {
                    // The PUG join promoted them server-side; reload to refetch /me.
                    window.location.hash = "Home";
                    window.location.reload();
                    return;
                  }
                  setPugSrData(null);
                  window.location.hash = "Home";
                  setActiveTab("Home");
                }}
              />
            )}

            {/* DISCORD JOIN REQUEST FINALIZE PAGE */}
            {activeTab === "JoinRequest" && joinRequestToken && (
              <JoinRequestPage
                token={joinRequestToken}
                me={me}
                onDone={() => {
                  sessionStorage.removeItem("pending_join_request_token");
                  if (me?.access_tier === "waitlisted") {
                    // Still waitlisted until an officer approves; reload so the
                    // waitlist gate takes over with fresh /me state.
                    window.location.hash = "Home";
                    window.location.reload();
                    return;
                  }
                  setJoinRequestToken(null);
                  window.location.hash = "Roster";
                  setActiveTab("Roster");
                }}
              />
            )}

            {/* INVITE LANDING PAGE */}
            {activeTab === "Invite" && inviteCode && (
              <InviteLandingPage
                code={inviteCode}
                me={me}
                onJoinSuccess={() => {
                  if (me?.access_tier === "waitlisted") {
                    // The join promoted them server-side; reload to refetch /me
                    // so the waitlist gate sees the new tier.
                    window.location.hash = "Guilds";
                    window.location.reload();
                    return;
                  }
                  setInviteCode(null);
                  window.location.hash = "Guilds";
                  setActiveTab("Guilds");
                }}
              />
            )}

{activeTab === "Home" && (
  me ? (
    /* LOGGED IN DASHBOARD - SINGLE COLUMN LAYOUT */
    <ThemedPage>
    <div className="relative">
      <div className="flex flex-col gap-8 max-w-5xl mx-auto">

      {/* HEADER: the Alignment sign up page's eyebrow + display title + amber
          hairline, with the light/dark switch. */}
      <header className="flex items-end justify-between gap-6 pt-2 sm:pt-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--c-accent-ink)]">Home</div>
          <h1 className="mt-2 text-4xl leading-tight text-[var(--c-ink)] sm:text-5xl" style={DISPLAY}>
            Welcome back{me.username ? `, ${me.username}` : ""}
          </h1>
          <div className="mt-4 h-px w-28 bg-gradient-to-r from-[var(--c-accent)] to-transparent" />
        </div>
      </header>

      {/* 1. ANNOUNCEMENTS - HEADER MATCHING RAID SCHEDULE */}
      <div className="w-full border-b border-[var(--c-line-soft)] pb-4 mb-2">
                       <div className="flex flex-wrap items-center gap-x-4">
                          
                         
                          
                          {/* FEED CONTENT */}
                          <div className="flex flex-wrap items-center pt-0.5">
                             <AnnouncementsFeed />
                          </div>
                          <div className="ml-auto">
                            <PatchNotesViewer />
                          </div>
                       </div>
                    </div>

        {/* 1b. CROSS-GUILD RAID INVITATIONS — shown only when the released player has pending invites */}
        <CrossGuildInvitesHome
          isAuthed={!!me}
          onResponded={() => setDataVersion(v => v + 1)}
          variant="desktop"
        />

        {/* 1b-i. OPEN SEATS: public seats in raids this player qualifies for. */}
        <OpenSeatsHome onChanged={() => setDataVersion(v => v + 1)} />

        {/* 1b-ii. YOUR SETUP + WEEKLY LOCKOUTS — one card. The readiness bar is
             the profile page's checklist laid out sideways, showing only what's
             blocking you, and it acts as the header for the lockout rail below
             it: sliding-cupboard portraits whose raids open inline between one
             portrait and the next. Rallify raids are the source of truth;
             players who raided outside Rallify tap an open raid tile to mark
             that character saved. Sits above the guild bar so your own setup
             comes first. The bar hides itself until you have a character, but
             the rail still renders. */}
        <PlayerReadyBar
          characters={characters}
          accountAvailability={accountAvailability}
          weeklyAvailability={weeklyAvailability}
          setWeeklyAvailability={setWeeklyAvailability}
        >
          <WeeklyLockouts refreshKey={dataVersion} />
        </PlayerReadyBar>

        {/* 1c. YOUR GUILDS COMMAND CENTER — one-click shortcuts into each managed
             guild's dashboard sub-tabs. Renders nothing for non-officer players. */}
        <GuildCommandCenter guilds={managedGuilds} />

        {/* 2. HERO: NEXT RAID */}
        <section aria-label="Next Raid">
          {nextRaid ? (
            <div 
              onClick={() => { sessionStorage.setItem('pending_open_raid', nextRaid.id); window.location.hash = "Planner"; }}
              className="lfm-dark group relative overflow-hidden rounded-3xl border border-[var(--c-line)] shadow-[var(--c-shadow-lg)] transition-all hover:border-[var(--c-accent-line)] h-[280px] flex flex-col justify-end bg-[var(--c-surface)] text-[var(--c-ink)] cursor-pointer"
            >
              {/* BACKGROUND LAYER */}
              <div className="absolute inset-0">
                  {nextRaid.raidImageUrl ? (
                      <div className="absolute inset-0 animate-in fade-in duration-700">
                          <img 
                              src={nextRaid.raidImageUrl} 
                              alt="" 
                              className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-1000 ease-out"
                          />
                      </div>
                  ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-indigo-950/40 to-transparent" />
                  )}
                  {/* Text Protection Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0c10] via-[#0a0c10]/70 to-transparent pointer-events-none" />
              </div>

              {/* TOP RIGHT: Countdown + Region */}
              <div className="absolute top-4 right-4 z-10 flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-black/60 border border-[var(--c-line)] backdrop-blur-sm">
                      <svg className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      <span className={`text-xs font-bold tabular-nums ${isNextRaidLive ? 'text-red-600 dark:text-red-400' : 'text-[var(--c-ink)]'}`}>
                          {isNextRaidLive
                            ? "🔴 LIVE"
                            : formatCountdownToMs(nextRaid.anchorDate.getTime(), Date.now())}
                      </span>
                  </div>
                  <div className="text-[10px] font-medium text-[var(--c-ink-3)] bg-[var(--c-surface)] px-2 py-1 rounded backdrop-blur-sm">
                      {nextRaid.region} • {nextRaid.realm}
                  </div>
              </div>

              {/* TOP LEFT: Difficulty + Size Badges */}
              <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border backdrop-blur-sm ${
                      nextRaid.difficulty === 'Mythic' ? 'border-orange-500/40 bg-orange-500/20 text-orange-700 dark:text-orange-300' :
                      nextRaid.difficulty === 'Heroic' ? 'border-purple-500/40 bg-purple-500/20 text-purple-700 dark:text-purple-300' :
                      nextRaid.difficulty === 'Ascended' ? 'border-red-500/40 bg-red-500/20 text-red-700 dark:text-red-300' :
                      'border-[var(--c-line-strong)] bg-[var(--c-sunken-2)] text-[var(--c-ink-2)]'
                  }`}>
                      {nextRaid.difficulty}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border border-[var(--c-line-strong)] bg-[var(--c-surface)] text-[var(--c-ink-2)] backdrop-blur-sm">
                      {nextRaid.size}-Man
                  </span>
                  {nextRaid.sessions && nextRaid.sessions.length > 1 && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border border-cyan-500/30 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 backdrop-blur-sm">
                          {nextRaid.sessions.length} Sessions
                      </span>
                  )}
              </div>
              
              {/* BOTTOM CONTENT */}
              <div className="relative p-6 md:p-8 z-10">
                  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                      {/* Left: Main Info */}
                      <div className="space-y-3 flex-1">
                          <div className="flex items-center gap-3">
                              <span className="inline-flex items-center gap-2 rounded-full bg-indigo-500/20 border border-indigo-400/30 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-indigo-800 dark:text-indigo-200 backdrop-blur-md">
                                  <span className="relative flex h-1.5 w-1.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-indigo-500"></span>
                                  </span>
                                  Next Raid
                              </span>
                          </div>
                          
                          <h2 className="text-2xl md:text-4xl font-black text-[var(--c-ink)] tracking-tight drop-shadow-2xl leading-[1.1]">
                              {nextRaid.raidName}
                          </h2>
                          
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                              {/* Date/Time */}
                              <div className="flex items-center gap-2 text-[var(--c-ink)] font-semibold text-sm">
                                  <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                  {nextRaid.localTimeLabel}
                              </div>
                              {/* Guild */}
                              <div className="flex items-center gap-2 text-sm text-[var(--c-ink-2)]">
                                  {nextRaid.guildLogo ? (
                                      <img src={nextRaid.guildLogo} alt="" className="h-5 w-5 rounded-full border border-[var(--c-line-strong)] bg-[var(--c-sunken-2)] object-cover" onError={(e) => e.currentTarget.style.display='none'} />
                                  ) : (
                                      <div className="h-5 w-5 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-[10px] text-indigo-700 dark:text-indigo-300 font-bold">
                                          {nextRaid.hostedByGuild.charAt(0).toUpperCase()}
                                      </div>
                                  )}
                                  <span className="font-medium">{nextRaid.hostedByGuild}</span>
                              </div>
                              {/* Raid Leader */}
                              {nextRaid.raidLeader && nextRaid.raidLeader !== 'TBD' && (
                                  <div className="flex items-center gap-1.5 text-sm text-[var(--c-ink-3)]">
                                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                      <span>Led by <span className="text-[var(--c-ink-2)] font-medium">{nextRaid.raidLeader}</span></span>
                                  </div>
                              )}
                          </div>
                      </div>

                      {/* Right: Your Assignment */}
                      {nextRaid.myCharName && (
                          <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[var(--c-sunken)] border border-[var(--c-line)] backdrop-blur-sm">
                                  {nextRaid.myCharImage ? (
                                      <div className="relative">
                                          <img src={nextRaid.myCharImage} alt="" className="h-10 w-10 rounded-lg border border-[var(--c-line-strong)] object-cover" />
                                          <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-[var(--c-surface)] border border-[var(--c-line-strong)] flex items-center justify-center text-[9px]">
                                          {nextRaid.myCharRole === 'Tank' ? '🛡' : nextRaid.myCharRole === 'Healer' ? '💚' : nextRaid.myCharRole === 'Melee DPS' ? '⚔️' : nextRaid.myCharRole === 'Ranged DPS' ? '🏹' : '👤'}
                                          </div>
                                      </div>
                                  ) : (
                                      <div className="h-10 w-10 rounded-lg bg-[var(--c-surface)] border border-[var(--c-line-strong)] flex items-center justify-center text-xl">
                                          {nextRaid.myCharRole === 'Tank' ? '\u{1F6E1}' : nextRaid.myCharRole === 'Healer' ? '\u{1F49A}' : nextRaid.myCharRole === 'Melee DPS' ? '\u2694\uFE0F' : nextRaid.myCharRole === 'Ranged DPS' ? '\u{1F3F9}' : '\u{1F464}'}
                                      </div>
                                  )}
                                  <div className="min-w-0">
                                      <div className="text-[9px] font-bold text-[var(--c-ink-3)] uppercase tracking-wider">Your Role</div>
                                      <div className="text-sm font-bold text-[var(--c-ink)] truncate" style={{ color: CLASS_COLORS[nextRaid.myCharClass || ''] || '#fff' }}>
                                          {nextRaid.myCharName}
                                      </div>
                                      <div className="text-[10px] text-[var(--c-ink-3)]">{nextRaid.myCharRole}</div>
                                  </div>
                              </div>
                              {nextRaid.srEnabled && (
                                  <div 
                                  onClick={(e) => { e.stopPropagation(); sessionStorage.setItem('pending_sr_raid', nextRaid.id); window.location.hash = "Planner"; }}
                                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-500/10 border border-purple-500/20 backdrop-blur-sm cursor-pointer hover:bg-purple-500/20 transition-colors"
                                  >
                                      <span className="text-purple-600 dark:text-purple-400 text-xs">&#x1F4DC;</span>
                                      <div className="flex-1 min-w-0">
                                          <div className="text-[9px] font-bold text-purple-700/60 dark:text-purple-300/60 uppercase tracking-wider">Soft Reserves</div>
                                          <div className="text-xs font-bold text-purple-800 dark:text-purple-200">
                                              {nextRaid.srMyCount || 0} / {nextRaid.srMax} SR
                                              {nextRaid.srLocked && <span className="ml-1.5 text-red-600/70 dark:text-red-400/70 text-[9px]">🔒 Locked</span>}
                                          </div>
                                          {nextRaid.srMyItems && nextRaid.srMyItems.length > 0 && (
                                              <div className="text-[10px] text-[var(--c-ink-3)] truncate mt-0.5">
                                                  {nextRaid.srMyItems.join(', ')}
                                              </div>
                                          )}
                                      </div>
                                      <span className="text-[10px] text-purple-600/50 dark:text-purple-400/50 font-bold">View &#x203A;</span>
                                  </div>
                              )}
                          </div>
                      )}
                  </div>
              </div>
            </div>
          ) : (
            <>
            {/* Guild Raids You're Not In */}
            {guildRaidOverview.length > 0 && (
              <div className="relative overflow-hidden rounded-2xl border border-amber-500/20 bg-amber-500/[0.03] p-5 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-amber-700 dark:text-amber-400 text-base">⚔️</span>
                  <h3 className="text-sm font-bold text-amber-700/90 dark:text-amber-300/90 uppercase tracking-wider">Your Guild Has Raids Planned</h3>
                </div>
                <div className="space-y-2.5">
                  {guildRaidOverview.map((gr: any) => {
                    const sessions = gr.sessions || [];
                    const firstSession = sessions[0];
                    const startDate = firstSession ? new Date(firstSession.starts_at) : null;
                    const nowMs = Date.now();
                    const startMs = startDate ? startDate.getTime() : 0;
                    const lastSess = sessions[sessions.length - 1];
                    const endMs = lastSess ? new Date(lastSess.starts_at).getTime() + (lastSess.duration || 180) * 60000 : 0;
                    const isLive = startMs > 0 && nowMs >= startMs && nowMs < endMs;

                    return (
                      <div
                        key={gr.id}
                        onClick={() => { window.location.hash = "Planner"; }}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          isLive 
                            ? 'bg-red-500/10 border border-red-500/20 hover:bg-red-500/15' 
                            : 'bg-[var(--c-bg-veil)] border border-[var(--c-line-soft)] hover:bg-[var(--c-sunken)]'
                        }`}
                      >
                        {gr.raid_image_url ? (
                          <img src={gr.raid_image_url} alt="" className="h-10 w-10 rounded-lg object-cover border border-[var(--c-line)]" />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-[var(--c-sunken)] border border-[var(--c-line)] flex items-center justify-center text-lg opacity-30">⚔️</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-[var(--c-ink)] truncate">{gr.raid_name}</span>
                            {isLive && (
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold text-red-600 dark:text-red-400 uppercase bg-red-400/10 px-1.5 py-0.5 rounded border border-red-400/20">
                                <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500"></span></span>
                                Live
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 text-[10px] text-[var(--c-ink-3)]">
                            {gr.guild_logo_url && (
                              <img src={gr.guild_logo_url} alt="" className="h-3.5 w-3.5 rounded-full border border-[var(--c-line)]" onError={(e: any) => e.currentTarget.style.display='none'} />
                            )}
                            <span>{gr.guild_tag || gr.guild_name}</span>
                            <span className="text-[var(--c-ink-4)]">•</span>
                            <span>{gr.assigned_count || 0}/{gr.raid_size || '?'} signed</span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          {startDate && (
                            <>
                              <div className="text-xs font-bold text-[var(--c-ink-2)]">
                                {startDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                              </div>
                              <div className="text-[10px] text-cyan-700/70 dark:text-cyan-400/70 font-medium">
                                {startDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[var(--c-ink-4)] mt-3 text-center">You're not currently assigned to these raids — check with your guild leader</p>
              </div>
            )}

{!hasEverRaided && (
            <div className="relative overflow-hidden rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-6">
               <h3 className="text-lg font-bold text-[var(--c-ink)] mb-4">Getting Started</h3>
               <div className="space-y-3">
                 <div 
                   onClick={() => window.location.hash = "Roster"} 
                   className="flex items-center gap-3 p-3 rounded-lg bg-[var(--c-bg-veil)] hover:bg-[var(--c-sunken)] border border-[var(--c-line-soft)] cursor-pointer transition-colors"
                 >
                   <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${characters.length > 0 ? 'bg-green-500/20 text-green-700 dark:text-green-400 border border-green-500/30' : 'bg-[var(--c-sunken-2)] text-[var(--c-ink-3)] border border-[var(--c-line)]'}`}>
                     {characters.length > 0 ? '✓' : '1'}
                   </div>
                   <div className="flex-1">
                     <div className="text-sm font-medium text-[var(--c-ink)]">Create a Character</div>
                     <div className="text-xs text-[var(--c-ink-3)]">Add your characters to the roster</div>
                   </div>
                 </div>
                 <div 
                   onClick={() => window.location.hash = "Roster"} 
                   className="flex items-center gap-3 p-3 rounded-lg bg-[var(--c-bg-veil)] hover:bg-[var(--c-sunken)] border border-[var(--c-line-soft)] cursor-pointer transition-colors"
                 >
                   <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${accountAvailability.some(v => v) ? 'bg-green-500/20 text-green-700 dark:text-green-400 border border-green-500/30' : 'bg-[var(--c-sunken-2)] text-[var(--c-ink-3)] border border-[var(--c-line)]'}`}>
                     {accountAvailability.some(v => v) ? '✓' : '2'}
                   </div>
                   <div className="flex-1">
                   <div className="text-sm font-medium text-[var(--c-ink)]">Set Your Availability</div>
                     <div className="text-xs text-[var(--c-ink-3)]">Mark when you can raid</div>
                   </div>
                 </div>
                 <div 
                   onClick={() => window.location.hash = "Guilds"}
                   className="flex items-center gap-3 p-3 rounded-lg bg-[var(--c-bg-veil)] hover:bg-[var(--c-sunken)] border border-[var(--c-line-soft)] cursor-pointer transition-colors"
                 >
                   <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${plannerGuilds.length > 0 ? 'bg-green-500/20 text-green-700 dark:text-green-400 border border-green-500/30' : 'bg-[var(--c-sunken-2)] text-[var(--c-ink-3)] border border-[var(--c-line)]'}`}>
                     {plannerGuilds.length > 0 ? '✓' : '3'}
                   </div>
                   <div className="flex-1">
                     <div className="text-sm font-medium text-[var(--c-ink)]">Join or Create a Guild <span className="text-[var(--c-ink-4)] text-xs">(Optional)</span></div>
                     <div className="text-xs text-[var(--c-ink-3)]">You can skip this if you aren't a guild leader</div>
                   </div>
                 </div>
                 <div 
                   onClick={() => window.location.hash = "Planner"} 
                   className="flex items-center gap-3 p-3 rounded-lg bg-[var(--c-bg-veil)] hover:bg-[var(--c-sunken)] border border-[var(--c-line-soft)] cursor-pointer transition-colors"
                 >
                   <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${scheduledRaids.length > 0 ? 'bg-green-500/20 text-green-700 dark:text-green-400 border border-green-500/30' : 'bg-[var(--c-sunken-2)] text-[var(--c-ink-3)] border border-[var(--c-line)]'}`}>
                     {scheduledRaids.length > 0 ? '✓' : '4'}
                   </div>
                   <div className="flex-1">
                     <div className="text-sm font-medium text-[var(--c-ink)]">Create a Raid <span className="text-[var(--c-ink-4)] text-xs">(Optional)</span></div>
                     <div className="text-xs text-[var(--c-ink-3)]">You can skip this if you aren't a raid or guild leader</div>
                   </div>
                 </div>
               </div>
               </div>
            )}
            {/* Fallback for veteran users with nothing to show */}
            {hasEverRaided && guildRaidOverview.length === 0 && (
              <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-8 text-center">
                <div className="text-2xl mb-2">⚔️</div>
                <div className="text-sm text-[var(--c-ink-3)] font-medium">No raids on the horizon</div>
                <div className="text-xs text-[var(--c-ink-4)] mt-1">Check the Planner to schedule your next raid</div>
              </div>
            )}
            </>
          )}
        </section>

        {/* 3. SCHEDULE: LIST VIEW */}
        <section aria-label="Schedule">
          <div className="flex items-center justify-between mb-4 px-2">
            <h3 className="text-lg text-[var(--c-ink)]" style={DISPLAY}>
              Raid schedule
            </h3>
            <div className="relative group z-30">
              <div className="inline-flex items-center gap-1.5 text-[11px] text-cyan-800/85 dark:text-cyan-200/85 border border-cyan-500/25 bg-cyan-500/8 rounded-md px-2 py-1">
                <svg className="w-3.5 h-3.5 text-cyan-700/70 dark:text-cyan-300/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="uppercase tracking-wide">Timezone:</span>
                <span className="font-bold">{scheduleTzShort}</span>
              </div>
              <div className="pointer-events-none absolute right-0 top-full z-40 mt-2 w-max max-w-[220px] rounded-md border border-cyan-500/25 bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] px-2.5 py-1.5 text-[11px] text-cyan-800 dark:text-cyan-100 shadow-[0_8px_24px_rgba(0,0,0,0.45)] opacity-0 translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0">
                <span className="text-cyan-700/80 dark:text-cyan-300/80">Time in {tzSnapshot}:</span>{" "}
                <span className="font-semibold">{homeClockInTz}</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            {scheduledRaids.length > 0 ? (
             scheduledRaids.map((r) => (
              <div 
                key={r.id} 
                onClick={() => { sessionStorage.setItem('pending_open_raid', r.id); window.location.hash = "Planner"; }}
                className="group relative flex items-stretch overflow-hidden rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow-sm)] cursor-pointer transition-all duration-200 hover:-translate-y-px hover:border-[var(--c-accent-line)] hover:shadow-[var(--c-shadow-lg)] h-24"
              >
                {/* Left: Image Area */}
                <div className="relative w-40 md:w-64 shrink-0">
                  {r.raidImageUrl ? (
                    <div className="absolute inset-0 h-full w-full">
                      <img 
                        src={r.raidImageUrl} 
                        alt="" 
                        className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                        style={{ 
                          // Gradual fade from solid to transparent
                          maskImage: 'linear-gradient(to right, black 0%, black 30%, transparent 100%)',
                          WebkitMaskImage: 'linear-gradient(to right, black 0%, black 30%, transparent 100%)'
                      }}
                      />
                    </div>
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/10 to-transparent flex items-center justify-center">
                        <span className="text-2xl opacity-10">⚔️</span>
                    </div>
                  )}
                </div>

                {/* Center: Info */}
                <div className="relative flex-1 py-2 pr-4 pl-0 flex flex-col justify-center min-w-0 z-10">
                  <div className="flex items-center gap-2 mb-1">
                  {(() => {
                      const nowMs = new Date().getTime();
                      const isLive = nowMs >= r.anchorDate.getTime() && nowMs < r.endDate.getTime();
                      const isAssigned = Boolean(r.myCharName);
                      return isLive ? (
                        <span className="inline-flex items-center gap-1.5 text-[9px] font-bold text-red-600 dark:text-red-400 uppercase tracking-wider bg-red-400/10 px-1.5 py-0.5 rounded border border-red-400/20">
                          <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500"></span></span>
                          Live
                        </span>
                      ) : isAssigned ? (
                        <span className="inline-flex items-center gap-1.5 text-[9px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider bg-emerald-400/10 px-1.5 py-0.5 rounded border border-emerald-400/20">
                          <span className="w-1 h-1 rounded-full bg-emerald-400 shadow-[0_0_8px_currentColor]"></span>
                          Signed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-[9px] font-bold text-[var(--c-ink-2)] uppercase tracking-wider bg-[var(--c-sunken)] px-1.5 py-0.5 rounded border border-[var(--c-line-strong)]">
                          <span className="w-1 h-1 rounded-full bg-white/45"></span>
                          Scheduled
                        </span>
                      );
                    })()}
                    {r.myCharName && (
                      <LegibleSpan className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded border border-[var(--c-line)] bg-[var(--c-sunken)]" color={CLASS_COLORS[r.myCharClass || ''] || '#aaa'}>
                        {r.myCharRole === 'Tank' ? '\u{1F6E1}' : r.myCharRole === 'Healer' ? '\u{1F49A}' : r.myCharRole === 'Melee DPS' ? '\u2694\uFE0F' : r.myCharRole === 'Ranged DPS' ? '\u{1F3F9}' : '\u{1F464}'}
                        {r.myCharName}
                      </LegibleSpan>
                    )}
                  </div>
                  
                  <h4 className="flex items-center gap-2 font-bold text-base text-[var(--c-ink)] group-hover:text-[var(--c-accent-ink)] transition-colors truncate tracking-tight">
                    <span className="truncate">{r.raidName}</span>
                    {/* Which game, only when the schedule mixes games (e.g. + WoW Forever). */}
                    {new Set(scheduledRaids.map((x) => x.gameVersion)).size > 1 && r.gameVersion && (
                      <span className="shrink-0 rounded border border-[var(--c-accent-line)] bg-[var(--c-accent-soft)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[var(--c-accent-ink)]">
                        {r.gameVersion}
                      </span>
                    )}
                  </h4>
                  
                  <div className="flex items-center gap-2 text-[11px] text-[var(--c-ink-3)] mt-0.5">
                      {r.guildLogo && (
                          <img src={r.guildLogo} alt="" className="h-4 w-4 rounded-full border border-[var(--c-line)] bg-[var(--c-sunken-2)] object-cover" onError={(e) => e.currentTarget.style.display='none'} />
                      )}
                      <span className="truncate max-w-[150px]">{r.hostedByGuild}</span>
                      <span className="text-[var(--c-ink-4)]">•</span>
                      <span>{r.difficulty}</span>
                  </div>
                </div>

                {/* Right: Date/Time + SR */}
                <div className="shrink-0 py-2 px-4 flex flex-col items-end justify-center text-right border-l border-[var(--c-line-soft)] bg-[var(--c-bg-veil)]">
                  {(() => {
                    const sched = formatScheduleRaidTimes(
                      r.anchorDate.getTime(),
                      r.endDate.getTime(),
                      tzSnapshot,
                      localNow.getTime()
                    );
                    return (
                      <>
                        <div className="text-[10px] font-bold text-[var(--c-ink-3)] uppercase tracking-wider">
                          {sched.weekday}
                        </div>
                        <div className="text-[12px] font-bold text-[var(--c-ink)] tabular-nums leading-tight">
                          {sched.startTime} - {sched.endTime}
                        </div>
                        <div className="text-[10px] text-cyan-700 dark:text-cyan-400 font-bold uppercase tracking-wider leading-tight">
                          {sched.dayLabel}
                        </div>
                      </>
                    );
                  })()}
                  {r.srEnabled && (
                    <div className={`mt-1 text-[9px] font-bold px-1.5 py-0.5 rounded border ${
                      (r.srMyCount || 0) >= (r.srMax || 1)
                        ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-400'
                        : r.srLocked
                        ? 'bg-red-500/10 border-red-500/20 text-red-600 dark:text-red-400'
                        : 'bg-purple-500/10 border-purple-500/20 text-purple-700 dark:text-purple-300'
                    }`}>
                      SR {r.srMyCount || 0}/{r.srMax}
                      {r.srLocked && ' 🔒'}
                    </div>
                  )}
                </div>
              </div>
            ))
            ) : (
              <div className="rounded-xl border border-dashed border-[var(--c-line)] bg-[var(--c-bg-veil)] p-8 text-center">
                  <p className="text-[15px] text-[var(--c-ink-3)]">No raids scheduled yet.</p>
              </div>
            )}
          </div>
        </section>

      </div>
    </div>
    </ThemedPage>
  ) : (
    // LOGGED OUT LANDING
    <LandingPage onTryIt={() => setTrialActive(true)} />
  )
)}

{activeTab === "Find" && (
              <ThemedPage>
              <div className="max-w-5xl mx-auto">
                <header className="mb-8 flex items-end justify-between gap-6 pt-2 sm:pt-4">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--c-accent-ink)]">Find</div>
                    <h1 className="mt-2 text-4xl leading-tight text-[var(--c-ink)] sm:text-5xl" style={DISPLAY}>Find a group</h1>
                    <div className="mt-4 h-px w-28 bg-gradient-to-r from-[var(--c-accent)] to-transparent" />
                    <p className="mt-4 text-[15px] text-[var(--c-ink-2)]">Players and guilds looking for each other.</p>
                  </div>
                </header>
                {/* Header with search and create button */}
                <div className="flex flex-col gap-3 mb-6">
                  {/* Top Row: Search and Create Button */}
                  <div className="flex items-center gap-3 w-full">
                    <input 
                      value={query} 
                      onChange={e => setQuery(e.target.value)} 
                      placeholder="Search listings..." 
                      className={R.input + " flex-1 py-2.5"}
                    />
                    {me && (
                      <button
                        onClick={() => setShowCreateLfgModal(true)}
                        className={R.btnPrimary + " shrink-0 whitespace-nowrap"}
                      >
                        + Create listing
                      </button>
                    )}
                  </div>

                  {/* Bottom Row: Filters */}
                  <div className="flex flex-wrap gap-1">
                    {LFG_TAGS.map(f => (
                      <button 
                        key={f} 
                        onClick={() => setActiveFilter(f)} 
                        className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${activeFilter === f ? 'bg-[var(--c-accent-soft)] border-[var(--c-accent)] text-[var(--c-accent-ink)] font-semibold' : 'border-[var(--c-line)] hover:bg-[var(--c-sunken)]'}`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Loading state */}
                {lfgLoading && (
                  <div className="text-center py-12 text-[var(--c-ink-3)]">Loading listings...</div>
                )}

                {/* Empty state */}
                {!lfgLoading && filteredListings.length === 0 && (
                  <div className="text-center py-12">
                    <div className="text-[var(--c-ink-3)] mb-4">
                      {lfgListings.length === 0 
                        ? "No listings yet. Be the first to create one!" 
                        : "No listings match your search."}
                    </div>
                    {me && lfgListings.length === 0 && (
                      <button
                        onClick={() => setShowCreateLfgModal(true)}
                        className={R.btnPrimary + " px-6 py-3"}
                      >
                        Create First Listing
                      </button>
                    )}
                  </div>
                )}

                {/* Listings grid */}
                {!lfgLoading && filteredListings.length > 0 && (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {filteredListings.map(l => (
                      <LfgListingCard 
                        key={l.id} 
                        listing={l} 
                        onClick={() => setSelectedLfgListing(l)}
                      />
                    ))}
                  </div>
                )}

                {/* Create Listing Modal */}
                {showCreateLfgModal && (
                  <CreateLfgModal 
                    characters={characters}
                    onClose={() => setShowCreateLfgModal(false)}
                    onSuccess={() => {
                      setShowCreateLfgModal(false);
                      fetchLfgListings();
                    }}
                  />
                )}

                {/* Listing Detail Modal */}
                {selectedLfgListing && (
                  <LfgDetailModal
                    listing={selectedLfgListing}
                    onClose={() => setSelectedLfgListing(null)}
                  />
                )}
              </div>
              </ThemedPage>
            )}

            {activeTab === "Roster" && (
              <ThemedPage>
              <RosterPage
                characters={characters}
                setCharacters={setCharacters}
                accountAvailability={accountAvailability}
                setAccountAvailability={setAccountAvailability}
                availabilityConfirmation={availabilityConfirmation}
                setAvailabilityConfirmation={setAvailabilityConfirmation}
                weeklyAvailability={weeklyAvailability}
                setWeeklyAvailability={setWeeklyAvailability}
              />
              </ThemedPage>
            )}

            {activeTab === "Guilds" && <ThemedPage><GuildsPage /></ThemedPage>}

            {/* Alignment Board: collect what people will play in WoW Forever,
                then build comps out of it. Standalone by design, see
                md/CLASS_ALIGNMENT_PLAN.md. */}
            {activeTab === "Alignment" && (
              <ThemedPage>
              
              {/* Officers get the board. Everybody else gets their own answers
                  and the guild's running totals, which is all a member needs
                  and all they should see. */}
              {plannerGuilds.length > 0 || adminViewGuild ? (
                <AlignmentBoardPage
                  guilds={(adminViewGuild ? [adminViewGuild] : plannerGuilds).map((g: any) => ({
                    id: String(g.id),
                    name: g.name,
                  }))}
                />
              ) : (
                <AlignmentPlayerView />
              )}
              </ThemedPage>
            )}

            {activeTab === "Planner" && (
              <PlannerPage
                key={adminViewGuild ? `admin-${adminViewGuild.id}` : "self"}
                guilds={adminViewGuild ? [adminViewGuild] : plannerGuilds}
                characters={characters}
                accountAvailability={accountAvailability}
                adminViewGuildId={adminViewGuild?.id ?? null}
              />
            )}

            {activeTab === "Admin" && isAdmin && <ThemedPage><AdminPanel /></ThemedPage>}
            {activeTab === "Waitlist" && isAdmin && <ThemedPage><WaitlistAdminPage /></ThemedPage>}
            {activeTab === "MILP" && isAdmin && <ThemedPage><MilpResultsPage /></ThemedPage>}
            {activeTab === "Bugs" && isAdmin && <ThemedPage><BugsAdminPage /></ThemedPage>}
            {activeTab === "Reports" && isAdmin && <ThemedPage><AdminRatingsPage /></ThemedPage>}
            {activeTab === "Announce" && isAdmin && <ThemedPage><AnnouncementsAdminPage /></ThemedPage>}
            {activeTab === "Patches" && isAdmin && <ThemedPage><PatchNotesAdminPage /></ThemedPage>}
            {activeTab === "Stats" && isAdmin && (
              <ThemedPage>
              
              <GuildStatsAdminPage
                onViewPlanner={(guild) => {
                  setAdminViewGuild(guild);
                  setActiveTab("Planner");
                  window.location.hash = "Planner";
                }}
              />
              </ThemedPage>
            )}
          </main>       
            
        </div>
        </div>

          {/* Bug Report Modal - with refresh callback */}
          <BugReportModal 
            isOpen={isBugModalOpen} 
            onClose={() => setIsBugModalOpen(false)}
            onSuccess={() => {
              // If user is on Bugs tab, refresh the page to show new bug
              if (activeTab === "Bugs") {
                window.location.reload();
              }
            }}
          />
        {/* Tutorial Helper - friendly knight guide */}
        <TutorialHelper
          currentTab={activeTab}
          isLoggedIn={!!me}
          hasGuild={plannerGuilds.length > 0}
          hasCharacters={characters.length > 0}
          hasRaids={scheduledRaids.length > 0}
          hasAvailability={accountAvailability.some(v => v)}
          spriteUrl="https://lfm-uploads.s3.eu-north-1.amazonaws.com/public/assets/knight-sprites.png"
        />
        {/* Onboarding Tour - one-time guided spotlight after creating a character */}
        {me && (
          <OnboardingTour
            currentTab={activeTab}
            onRequestTab={setActiveTab}
            availabilitySet={accountAvailability.some(v => v)}
          />
        )}
        {/* CHAT SIDEBAR - Fixed Top Right (Below Header) */}
        {me && (
          <div className="fixed top-24 right-6 z-50 flex flex-col items-end gap-3">
             <RaidChatSidebar />
          </div>
        )}

        {/* BUG REPORT BUTTON - Fixed Bottom Right */}
        {me && (
          <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
            <div className="shadow-lg shadow-black/50 rounded-full overflow-hidden">
              <BugReportButton onOpen={() => setIsBugModalOpen(true)} />
            </div>
          </div>
        )}
      </ConfirmProvider>
      </ToastProvider>
    </TimezoneProvider>
    </ThemeProvider>
  );
}

// --- Sub-components ---

function TabButton({ label, icon, active, onClick }: { label: string; icon: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button 
      onClick={onClick} 
      className={`
        relative px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-all duration-200 flex items-center gap-2
        ${active 
          ? "bg-[var(--c-surface)] text-[var(--c-accent-ink)] shadow-[var(--c-shadow)] ring-1 ring-[var(--c-line)]"
          : "text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:bg-[var(--c-surface)]"
        }
      `}
    >
      {icon}
      <span className="hidden xl:block">{label}</span>
    </button>
  );
}

// LFG Listing Card Component
function LfgListingCard({ listing, onClick }: { listing: LfgListing; onClick: () => void }) {
  const classColor = useTheme().onLight(CLASS_COLORS[listing.character_class] || '#888');
  
  return (
    <div 
      onClick={onClick}
      className="group relative flex flex-col rounded-2xl bg-[var(--c-surface)] overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-cyan-900/20 border border-[var(--c-line-soft)] hover:border-[var(--c-line)]"
    >
      {/* 1. Image Area with Gradient Overlay */}
      <div className="relative h-44 overflow-hidden"> {/* Increased height slightly for better aspect ratio */}
        {listing.image_url ? (
          <div 
            className="h-full w-full bg-cover bg-center transition-transform duration-700 group-hover:scale-110" 
            style={{ backgroundImage: `url(${listing.image_url})` }} 
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
            <span className="text-4xl opacity-20">⚔️</span>
          </div>
        )}
        
        {/* IMPROVED FADE: Solid bottom transition */}
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--c-surface)] via-[color-mix(in_srgb,var(--c-surface)_70%,transparent)] to-transparent" />
        
        {/* Tag Badge */}
        <div className="absolute top-3 left-3">
           <span className="px-2.5 py-1 rounded-md bg-black/60 border border-[var(--c-line)] backdrop-blur-md text-[10px] font-bold uppercase tracking-wider text-white shadow-lg">
            {listing.tag}
          </span>
        </div>
      </div>
      
      {/* 2. Content Area */}
      <div className="p-5 flex-1 flex flex-col relative -mt-8"> {/* Pulled up higher to sit inside the fade */}
        {/* Header: Title */}
        <div className="flex justify-between items-start gap-2 mb-1">
           <h3 className="font-bold text-lg text-[var(--c-ink)] leading-tight group-hover:text-cyan-700 dark:group-hover:text-cyan-400 transition-colors line-clamp-1 drop-shadow-md">
             {listing.name}
           </h3>
        </div>

        {/* Character Info */}
        <div className="flex items-center gap-2 mb-3 text-xs relative z-10">
          <div 
            className="w-2 h-2 rounded-full shadow-[0_0_8px_currentColor]"
            style={{ backgroundColor: classColor, color: classColor }}
          />
          <span style={{ color: classColor }} className="font-bold uppercase tracking-wide opacity-100 drop-shadow-sm">
            {listing.character_class}
          </span>
          <span className="text-[var(--c-ink-4)]">|</span>
          <span className="text-[var(--c-ink-2)]">{listing.character_name}</span>
        </div>
        
        {/* Description */}
        <p className="text-sm text-[var(--c-ink-2)] mb-5 line-clamp-2 leading-relaxed flex-1">
          {listing.description}
        </p>
        
        {/* Footer: Timer & Button */}
        <div className="mt-auto flex items-center justify-between gap-3 pt-4 border-t border-[var(--c-line-soft)]">
          <div className="flex items-center gap-1.5 text-xs text-[var(--c-ink-3)] font-mono">
             <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
             {getTimeRemaining(listing.expires_at)}
          </div>

          <a
            href={listing.discord_link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="pl-4 pr-3 py-1.5 rounded-lg bg-[#5865F2]/10 border border-[#5865F2]/20 text-[#5865F2] text-xs font-bold uppercase tracking-wide hover:bg-[#5865F2] hover:text-[var(--c-ink)] transition-all flex items-center gap-2 group/btn"
          >
            Join
            <svg className="w-3 h-3 transition-transform group-hover/btn:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
          </a>
        </div>
      </div>
    </div>
  );
}

// LFG Detail Modal Component
function LfgDetailModal({ listing, onClose }: { listing: LfgListing; onClose: () => void }) {
  const classColor = useTheme().onLight(CLASS_COLORS[listing.character_class] || '#888');
  const createdDate = new Date(listing.created_at);
  const expiresDate = new Date(listing.expires_at);
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-[var(--c-surface)] rounded-xl border border-[var(--c-line)] shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image header */}
        {listing.image_url ? (
          <div 
            className="h-48 bg-cover bg-center relative"
            style={{ backgroundImage: `url(${listing.image_url})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--c-surface)] to-transparent" />
            <button 
              onClick={onClose}
              className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/50 flex items-center justify-center text-white/80 hover:text-white hover:bg-black/70 transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="h-32 bg-gradient-to-br from-[var(--c-sunken)] to-[var(--c-bg-veil)] flex items-center justify-center relative">
            <span className="text-6xl opacity-20">🎮</span>
            <button 
              onClick={onClose}
              className="absolute top-3 right-3 h-8 w-8 rounded-full bg-[var(--c-surface)] flex items-center justify-center text-[var(--c-ink-2)] hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken-2)] transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Title and tag */}
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-xl font-bold leading-tight">{listing.name}</h2>
            <span className="px-3 py-1 rounded-full bg-cyan-500/20 border border-cyan-500/30 text-cyan-700 dark:text-cyan-300 text-xs font-medium shrink-0">
              {listing.tag}
            </span>
          </div>

          {/* Character info */}
          <div className="flex items-center gap-2 text-sm">
            <div className="h-8 w-8 rounded-full bg-[var(--c-sunken-2)] flex items-center justify-center text-lg">
              ⚔️
            </div>
            <div>
              <span style={{ color: classColor }} className="font-medium">{listing.character_name}</span>
              <span className="text-[var(--c-ink-4)] mx-2">·</span>
              <span className="text-[var(--c-ink-3)]">{listing.character_class}</span>
            </div>
          </div>
          {listing.character_realm && (
            <div className="text-xs text-[var(--c-ink-3)] -mt-2 ml-10">{listing.character_realm}</div>
          )}

          {/* Description */}
          <div className="bg-[var(--c-bg-veil)] rounded-lg p-4 border border-[var(--c-line-soft)]">
            <h3 className="text-xs text-[var(--c-ink-3)] uppercase tracking-wider mb-2">Description</h3>
            <p className="text-sm text-[var(--c-ink)] whitespace-pre-wrap leading-relaxed">{listing.description}</p>
          </div>

          {/* Timestamps */}
          <div className="flex items-center justify-between text-xs text-[var(--c-ink-3)]">
            <div>
              Posted {createdDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
            <div className="flex items-center gap-1">
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {getTimeRemaining(listing.expires_at)}
            </div>
          </div>

          {/* Discord button */}
          <a
            href={listing.discord_link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-[#5865F2] text-[var(--c-ink)] font-medium hover:bg-[#4752C4] transition-colors"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037 14.178 14.178 0 0 0-.624 1.29 18.423 18.423 0 0 0-4.825 0 14.12 14.12 0 0 0-.63-1.29.074.074 0 0 0-.077-.037 19.736 19.736 0 0 0-4.885 1.515.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.956 2.42-2.157 2.42zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.946 2.42-2.157 2.42z"/>
            </svg>
            Join via Discord
          </a>
        </div>
      </div>
    </div>
  );
}

// Create LFG Modal Component
function CreateLfgModal({ 
  characters, 
  onClose, 
  onSuccess 
}: { 
  characters: any[]; 
  onClose: () => void; 
  onSuccess: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [discordLink, setDiscordLink] = useState('');
  const [tag, setTag] = useState<string>('Casual');
  const [characterId, setCharacterId] = useState<string>('');
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeCharacters = characters.filter(c => c.is_active !== false);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return;
      }
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description);
      formData.append('discord_link', discordLink);
      formData.append('tag', tag);
      formData.append('character_id', characterId);
      if (image) {
        formData.append('image', image);
      }

      const res = await fetch('/api/lfg', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || data.error || 'Failed to create listing');
      }

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to create listing');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[var(--c-surface)] rounded-xl border border-[var(--c-line)] shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--c-line)]">
          <h2 className="text-lg font-bold">Create Listing</h2>
          <button onClick={onClose} className="text-[var(--c-ink-3)] hover:text-[var(--c-ink)] transition-colors">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-xs text-[var(--c-ink-3)] mb-1">Listing Name *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Farming Stratholme, LFM Warsong Gulch"
              maxLength={100}
              required
              className="w-full px-3 py-2 rounded-lg bg-[var(--c-sunken)] border border-[var(--c-line)] text-sm outline-none focus:border-cyan-500 transition-colors"
            />
          </div>

          {/* Tag */}
          <div>
            <label className="block text-xs text-[var(--c-ink-3)] mb-1">Category *</label>
            <div className="flex flex-wrap gap-2">
              {LFG_TAGS.filter(t => t !== 'All').map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTag(t)}
                  className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                    tag === t 
                      ? 'bg-cyan-500/20 border-cyan-500 text-cyan-800 dark:text-cyan-100' 
                      : 'border-[var(--c-line)] hover:bg-[var(--c-sunken)] text-[var(--c-ink-2)]'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs text-[var(--c-ink-3)] mb-1">Description *</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe what you're looking for, requirements, schedule, etc."
              maxLength={500}
              required
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-[var(--c-sunken)] border border-[var(--c-line)] text-sm outline-none focus:border-cyan-500 transition-colors resize-none"
            />
            <div className="text-right text-[10px] text-[var(--c-ink-4)] mt-1">{description.length}/500</div>
          </div>

          {/* Discord Link */}
          <div>
            <label className="block text-xs text-[var(--c-ink-3)] mb-1">Discord Invite Link *</label>
            <input
              type="url"
              value={discordLink}
              onChange={e => setDiscordLink(e.target.value)}
              placeholder="https://discord.gg/xxxxx"
              required
              className="w-full px-3 py-2 rounded-lg bg-[var(--c-sunken)] border border-[var(--c-line)] text-sm outline-none focus:border-cyan-500 transition-colors"
            />
            <div className="text-[10px] text-[var(--c-ink-4)] mt-1">People will use this to join your group</div>
          </div>

          {/* Character Selection */}
          <div>
            <label className="block text-xs text-[var(--c-ink-3)] mb-1">Display Character *</label>
            {activeCharacters.length === 0 ? (
              <div className="text-sm text-[var(--c-ink-3)] p-3 rounded-lg bg-[var(--c-sunken)] border border-[var(--c-line)]">
                You need to add a character first. Go to the Roster tab.
              </div>
            ) : (
              <select
                value={characterId}
                onChange={e => setCharacterId(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-lg bg-[var(--c-sunken)] border border-[var(--c-line)] text-sm outline-none focus:border-cyan-500 transition-colors"
              >
                <option value="" className="bg-[var(--c-surface)] text-[var(--c-ink)]">Select a character...</option>
                {activeCharacters.map(c => (
                  <option key={c.id} value={c.id} className="bg-[var(--c-surface)] text-[var(--c-ink)]">
                    {c.name} - {c.metadata?.class_name || 'Unknown'} ({c.metadata?.realm || 'Unknown Realm'})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-xs text-[var(--c-ink-3)] mb-1">Image (optional)</label>
            <div className="flex items-center gap-3">
              {imagePreview ? (
                <div className="relative">
                  <img src={imagePreview} alt="Preview" className="h-20 w-28 object-cover rounded-lg border border-[var(--c-line)]" />
                  <button
                    type="button"
                    onClick={() => { setImage(null); setImagePreview(null); }}
                    className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 flex items-center justify-center text-white text-xs"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center h-20 w-28 rounded-lg border border-dashed border-[var(--c-line-strong)] cursor-pointer hover:border-[var(--c-ink-4)] transition-colors">
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                  <span className="text-[var(--c-ink-4)] text-xs">+ Add Image</span>
                </label>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="text-[10px] text-[var(--c-ink-4)] p-3 rounded-lg bg-[var(--c-bg-veil)] border border-[var(--c-line-soft)]">
            📌 Listings automatically expire after 48 hours
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg border border-[var(--c-line)] text-[var(--c-ink-2)] text-sm font-medium hover:bg-[var(--c-sunken)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || activeCharacters.length === 0}
              className="flex-1 px-4 py-2.5 rounded-lg bg-cyan-500/20 border border-cyan-500/50 text-cyan-800 dark:text-cyan-100 text-sm font-medium hover:bg-cyan-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating...' : 'Create Listing'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Background() {
  return (
    <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(56,189,248,0.12),transparent),linear-gradient(to_bottom,rgba(10,14,22,1),rgba(6,10,18,1))]" />
    </div>
  );
}